import { MultiPickType } from 'blackbox';
import { useRef, useState } from 'react';
import Dropdown from '../../../common/Dropdown';
import Button from '../../../PendantButton';
import { COLOR } from '../../../shared/shared';
import { MultiPickData, SelectMode } from '../utilities/types';

type EditorButtonFunction = () => void;

export interface EditorBarItemProps {
	itemClass: string;
	title: string;
	disabled?: boolean;
	displayRed?: boolean;
	isActive?: boolean;
	action: EditorButtonFunction;
	pIconClass?: string;
}

export function EditorBarItem({
	itemClass,
	title,
	action,
	disabled,
	displayRed,
	isActive,
	pIconClass,
}: EditorBarItemProps) {
	return (
		<Button
			pClass={itemClass + ' not-round'}
			pAction={action}
			pDisabled={disabled}
			pClickRed={displayRed}
			pNormalColorOverride={isActive ? COLOR.LIGHT_BLUE : COLOR.TRANSPARENT}
			pClickColorOverride={COLOR.LIGHT_BLUE}
		>
			<div className="flex-col flex-center align-center">
				<span className={(pIconClass || '') + ' font-5 bold-font'}></span>
				<span className="half-padding-top"> {title} </span>
			</div>
		</Button>
	);
}

export interface EditorBarProps {
	editorAddBox: EditorButtonFunction;
	editorAddMultiPickBox: (pMultiPickData: MultiPickData) => void;
	showAuto: boolean;
	selectMode: SelectMode;
	toggleEditorSelect: EditorButtonFunction;
	editorRotateBox: EditorButtonFunction;
	editorCenter: EditorButtonFunction;
	editorDeleteBox: EditorButtonFunction;
	editorClearBoxes: EditorButtonFunction;
	setShowAuto: (pShowAuto: boolean) => void;
	toggleMoveBoxes: EditorButtonFunction;
	editorCloneBoxes: EditorButtonFunction;
	moveBoxes: boolean;
}

export function EditorBar({
	editorAddBox,
	editorAddMultiPickBox,
	editorCenter,
	editorClearBoxes,
	editorDeleteBox,
	editorRotateBox,
	showAuto,
	selectMode,
	toggleEditorSelect,
	setShowAuto,
	toggleMoveBoxes,
	editorCloneBoxes,
	moveBoxes,
}: EditorBarProps) {
	const lMultiPickRef = useRef<HTMLDivElement>(null);

	const [lSelectedMultiPick, lSetSelectedMultiPick] = useState<MultiPickData>({
		multipick: MultiPickType.None,
		alongWidth: false,
	});

	const getMPButtonText = (pData?: MultiPickData) => {
		if (!pData) pData = lSelectedMultiPick;
		switch (pData.multipick) {
			case MultiPickType.Double:
				return 'Double ' + (pData.alongWidth ? 'Width' : 'Length');
			case MultiPickType.Triple:
				return 'Triple ' + (pData.alongWidth ? 'Width' : 'Length');
			default:
				return 'Multipick';
		}
	};

	const handleAddMultiPick = (pMultiPickData: MultiPickData) => {
		if (!!lMultiPickRef.current?.click) {
			lMultiPickRef.current.click();
		}
		editorAddMultiPickBox(pMultiPickData);
	};

	return (
		<div className="width-98 flex-row flex-around align-center vh-8 background-medium-blue round">
			{selectMode !== SelectMode.Multi ? (
				<>
					<EditorBarItem
						itemClass="EditorItem"
						disabled={showAuto}
						action={editorAddBox}
						title="Add Box"
						pIconClass="icon-part"
					/>

					<Dropdown
						className="EditorItem"
						triggerRef={lMultiPickRef}
						isDropup={true}
						trigger={
							<div
								className={
									'full-size align-center ' +
									(lSelectedMultiPick.multipick !== MultiPickType.None
										? 'flex-row'
										: 'flex-col')
								}
							>
								{lSelectedMultiPick.multipick !== MultiPickType.None ? (
									<span className="color-blue background-white padding rounded margin-right ">
										{lSelectedMultiPick.multipick +
											1 +
											'x' +
											(lSelectedMultiPick.alongWidth ? 'W' : 'L')}
									</span>
								) : (
									<span
										className="icon-design font-5 bold-font"
										onClick={e => {
											if (lSelectedMultiPick.multipick !== MultiPickType.None) {
												// e.preventDefault();
												e.stopPropagation();
												editorAddMultiPickBox(lSelectedMultiPick);
											}
										}}
									></span>
								)}
								<span
									className="half-padding-top"
									onClick={e => {
										if (lSelectedMultiPick.multipick !== MultiPickType.None) {
											// e.preventDefault();
											e.stopPropagation();
											editorAddMultiPickBox(lSelectedMultiPick);
										}
									}}
								>
									{lSelectedMultiPick.multipick !== MultiPickType.None
										? 'Add'
										: 'Add Multipick'}
								</span>
							</div>
						}
						isRightAligned={true}
					>
						<div className="full-size">
							{Object.values(MultiPickType)
								.filter(
									(value): value is MultiPickType =>
										typeof value === 'number' && value !== MultiPickType.None
								)
								.map((pType, pIdx) => {
									const lengthObj: MultiPickData = {
										multipick: pType,
										alongWidth: false,
									};
									const widthObj: MultiPickData = { multipick: pType, alongWidth: true };
									return (
										<div key={pIdx}>
											<Button
												pClass="full-width margin-bottom"
												pAction={() => {
													handleAddMultiPick(lengthObj);
													lSetSelectedMultiPick(lengthObj);
												}}
											>
												<span>{getMPButtonText(lengthObj)}</span>
											</Button>
											<Button
												pClass="full-width margin-bottom"
												pAction={() => {
													handleAddMultiPick(widthObj);
													lSetSelectedMultiPick(widthObj);
												}}
											>
												<span>{getMPButtonText(widthObj)}</span>
											</Button>
										</div>
									);
								})}
						</div>
					</Dropdown>
				</>
			) : (
				<>
					<EditorBarItem
						itemClass="EditorItem"
						isActive={moveBoxes && !showAuto}
						disabled={showAuto}
						action={toggleMoveBoxes}
						title="Move Boxes"
						pIconClass="icon-tdof"
					/>
					<EditorBarItem
						itemClass="EditorItem"
						disabled={showAuto}
						action={editorCloneBoxes}
						title="Clone Boxes"
						pIconClass="icon-paste"
					/>
				</>
			)}

			<div className="height-80 w-2px background-silver"></div>
			<EditorBarItem
				itemClass="EditorItem"
				isActive={selectMode === SelectMode.Multi && !showAuto}
				disabled={showAuto}
				action={toggleEditorSelect}
				title="Multi-Select"
				pIconClass="icon-select"
			/>

			<EditorBarItem
				itemClass="EditorItem"
				disabled={showAuto}
				action={editorRotateBox}
				title="Rotate"
				pIconClass="icon-z-up"
			/>

			<EditorBarItem
				itemClass="EditorItem"
				disabled={showAuto}
				action={editorCenter}
				title="Center"
				pIconClass="icon-fit-to-view"
			/>
			<div className="height-80 w-2px background-silver"></div>
			<EditorBarItem
				itemClass="EditorItem"
				action={() => setShowAuto(!showAuto)}
				title={!showAuto ? 'Auto Layout' : 'Custom Layout'}
				pIconClass="icon-trigger"
			/>
			<EditorBarItem
				itemClass="EditorItem"
				disabled={showAuto}
				displayRed={true}
				action={editorDeleteBox}
				title="Delete"
				pIconClass="icon-delete"
			/>
			<EditorBarItem
				itemClass="EditorItem"
				disabled={showAuto}
				displayRed={true}
				action={editorClearBoxes}
				title="Clear All"
				pIconClass="icon-deselect"
			/>
		</div>
	);
}
